import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {Output, Component, Input, OnDestroy, OnInit, Optional, ViewChild, EventEmitter} from '@angular/core';
import { MatSortHeader } from '@angular/material/sort';
import { MatColumnDef, MatTable } from '@angular/material/table';

@Component({
    selector: 'app-data-column',
    template: `
    <ng-container matColumnDef>
        <th mat-header-cell *matHeaderCellDef mat-sort-header [disabled]="disabledSort"> {{ label }} </th>
        <td mat-cell *matCellDef="let data">
            <span *ngIf="!editable">
                {{ getData(data) }}
            </span>

            <span *ngIf="editable && !disableButton(data)">
                <button matSuffix mat-icon-button (click)="editClicked(data)" style="float: right;">
                    <mat-icon matTooltip="Edit User" style="right: -24px; position: relative;">edit</mat-icon>
                </button>
            </span>
            <span *ngIf="editable && disableButton(data)">
                <button mat-icon-button (click)="editClicked(data)" style="float: right;" [disabled]="disableButton(data)">
                    <mat-icon matTooltip="Cannot Edit your own Profile"
                        style="color: #0000001f; right: -24px; position: relative;">edit</mat-icon>
                </button>
            </span>
        </td>
    </ng-container>`
})
export class DataColumnComponent<T> implements OnDestroy, OnInit {

    /** Column name that should be used to reference this column. */
    @Input()
    get name(): string { return this._name; }
    set name(name: string) {
        this._name = name;
        this.columnDef.name = name;
    }
    _name: string;

    /**
     * Text label that should be used for the column header. If this property is not
     * set, the header text will default to the column name.
     */
    @Input() label: string;

    @Input() ignoreIfValue: string;

    /**
     * Accessor function to retrieve the data should be provided to the cell. If this
     * property is not set, the data cells will assume that the column name is the same
     * as the data property the cells should display.
     */
    @Input() dataAccessor: ((data: T, name: string) => string);

    /** Alignment of the cell values. */
    @Input() align: 'before' | 'after' = 'before';

    /** Whether the column is sortable */
    @Input()
    get disabledSort(): boolean { return this._disabledSort; }
    set disabledSort(disabledSort: boolean) {
        this._disabledSort = coerceBooleanProperty(disabledSort);
    }
    _disabledSort: boolean;

    /** Whether the column is editable */
    @Input()
    get editable(): boolean { return this._editable; }
    set editable(editable: boolean) {
        this._editable = coerceBooleanProperty(editable);
    }
    _editable: boolean;

    @Output()
    public editIconClicked: EventEmitter<T> = new EventEmitter<T>();

    @ViewChild(MatColumnDef, {static: true}) columnDef: MatColumnDef;

    @ViewChild(MatSortHeader, {static: true}) sortHeader: MatSortHeader;

    constructor(
        @Optional() public table: MatTable<any>
    ) {

    }

    ngOnInit() {
        // if no label is set, use name as label
        if (!this.label) {
            this.label = this.capitilize(this.name);
        }

        if (this.table) {
            this.table.addColumnDef(this.columnDef);
        }
    }

    ngOnDestroy() {
        if (this.table) {
            this.table.removeColumnDef(this.columnDef);
        }
    }

    getData(data: T): any {
        return this.dataAccessor ? this.dataAccessor(data, this.name) : (data as any)[this.name];
    }

    disableButton(data: T): boolean {
        return data[this.name] === this.ignoreIfValue;
    }

    capitilize(word: string) {
        word = word.replace(/_/g, ' ');
        return word[0].toUpperCase() + word.substr(1).toLowerCase();
    }

    editClicked(data: T) {
        this.editIconClicked.emit(data);
    }

}
